import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, API, formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";

export default function QRSignoff() {
  const { t } = useTranslation();
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [comments, setComments] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/verify/qr/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || t("qr.invalid")));
  }, [token]);

  const signoff = async (approve) => {
    if (!approve && !comments.trim()) return toast.error(t("qr.commentsRequired"));
    setBusy(true);
    try {
      await api.post(`/verify/qr/${token}/signoff?approve=${approve}&comments=${encodeURIComponent(comments)}`);
      setDone(approve ? "approved" : "declined");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const img = (path) => `${API}/files/${path}?auth=${token}`;

  return (
    <div className="min-h-screen bg-slate-50 p-4">
      <div className="mx-auto max-w-lg">
        <div className="mb-6 flex items-center gap-3 py-4">
          <div className="rounded-md bg-slate-900 px-2.5 py-1.5">
            <img src="/logo.webp" alt="The Retrofitter" className="h-6 w-auto" />
          </div>
          <div className="overline text-slate-500">{t("qr.terminal")}</div>
        </div>

        {error && <Card className="border-red-200 bg-red-50 p-6 text-center text-red-600" data-testid="qr-error">{error}</Card>}

        {done && (
          <Card className={`p-8 text-center ${done === "approved" ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`} data-testid="qr-done">
            {done === "approved" ? <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" /> : <XCircle className="mx-auto h-12 w-12 text-red-500" />}
            <h2 className="mt-3 font-heading text-2xl font-black text-slate-900">{done === "approved" ? t("qr.approved") : t("qr.declined")}</h2>
            <p className="mt-1 text-slate-500">{t("qr.recorded")}</p>
          </Card>
        )}

        {!error && !done && !data && <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>}

        {!done && data && (
          <div className="space-y-4">
            <Card className="border-slate-200 bg-white p-5 shadow-sm" data-testid="qr-review-card">
              <div className="mb-1 flex items-center gap-2 text-emerald-600"><ShieldCheck className="h-4 w-4" /><span className="overline">{t("qr.verifiedInstallation")}</span></div>
              <h1 className="font-heading text-2xl font-black text-slate-900">{data.installation.bus_number || data.installation.expected_bus_serial}</h1>
              <p className="text-slate-500">{data.installation.bus_type || data.installation.bus_variant} · {t("qr.unit")} {data.installation.expected_product_serial}</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {[["qr.before", "photos_before"], ["qr.after", "photos_after"]].map(([label, key]) => {
                  const list = data.installation[key] || [];
                  return (
                    <div key={key}>
                      <div className="overline mb-1.5 text-slate-500">{t(label)} ({list.length})</div>
                      <div className="grid grid-cols-2 gap-2">
                        {list.length ? list.map((p) => (
                          <img key={p} src={img(p)} alt={t(label)} className="w-full rounded-md border border-slate-200" />
                        )) : <div className="col-span-2 flex h-32 items-center justify-center rounded-md border border-dashed border-slate-300 text-slate-400">{t("qr.noPhoto")}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            <Textarea data-testid="qr-comments" value={comments} onChange={(e) => setComments(e.target.value)} placeholder={t("qr.commentsPlaceholder")} className="border-slate-300 bg-white" />
            <div className="grid grid-cols-2 gap-3">
              <Button data-testid="qr-decline" onClick={() => signoff(false)} disabled={busy} variant="outline" className="h-14 gap-2 border-red-300 text-red-600 hover:bg-red-50"><XCircle className="h-5 w-5" /> {t("qr.decline")}</Button>
              <Button data-testid="qr-approve" onClick={() => signoff(true)} disabled={busy} className="h-14 gap-2 bg-emerald-600 hover:bg-emerald-500"><CheckCircle2 className="h-5 w-5" /> {t("qr.approve")}</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
